import React, { useCallback, useState } from 'react';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { ChevronDown, X } from 'lucide-react';
import { DestinationItem, PortOption, countryRuleControl } from './countryRuleTypes';
const normalized = (value: string | null) => String(value || '').trim().toLocaleUpperCase();
export default function CountryRuleDestinations({
  value,
  ports,
  country,
  disabled,
  onChange
}: {
  value: DestinationItem[];
  ports: PortOption[];
  country: string;
  disabled: boolean;
  onChange: (items: DestinationItem[]) => void;
}) {
  const [query, setQuery] = useState('');
  const [manual, setManual] = useState('');
  const [error, setError] = useState('');
  const [showManual, setShowManual] = useState(false);
  const focusSearch = useCallback((input: HTMLInputElement | null) => input?.focus({
    preventScroll: true
  }), []);
  const candidates = ports.filter(port => normalized(port.country) === normalized(country));
  const matching = candidates.filter(port => port.label.toLocaleLowerCase().includes(query.trim().toLocaleLowerCase()));
  const addManual = () => {
    const label = manual.trim();
    if (!label) return;
    if (value.some(item => normalized(item.label) === normalized(label))) {
      setError('Deze bestemming staat al in de selectie.');
      return;
    }
    onChange([...value, {
      portRuleId: null,
      label
    }]);
    setManual('');
    setShowManual(false);
    setError('');
  };
  return <div className="space-y-3">
  <Popover><PopoverButton disabled={disabled} className="flex min-h-10 w-full items-center justify-between gap-3 border border-slate-300 bg-white px-3 py-2 text-left text-sm dark:border-white/20 dark:bg-slate-950" onClick={() => setQuery('')}>Ports/hubs/freezones selecteren<ChevronDown className="h-4 w-4" /></PopoverButton>
   <PopoverPanel portal anchor={{
        to: 'bottom start',
        gap: 4,
        padding: 8
      }} className="z-[100] w-[var(--button-width)] min-w-60 border border-slate-200 bg-white p-3 text-sm dark:border-white/10 dark:bg-slate-900">
    <input type="search" aria-label="Port zoeken" placeholder="Zoeken…" value={query} onChange={e => setQuery(e.target.value)} ref={focusSearch} className={countryRuleControl} />
    <div className="mt-3 max-h-60 overflow-y-auto">{matching.map(port => <label key={port.portRuleId} className="flex cursor-pointer items-center gap-3 py-2"><input type="checkbox" className="accent-orange-500" checked={value.some(item => item.portRuleId === port.portRuleId)} disabled={disabled} onChange={e => {
              setError('');
              onChange(e.target.checked ? [...value.filter(item => normalized(item.label) !== normalized(port.label)), {
                portRuleId: port.portRuleId,
                label: port.label
              }] : value.filter(item => item.portRuleId !== port.portRuleId));
            }} />{port.label}</label>)}{!matching.length && <p className="py-3 text-slate-500">Geen ports gevonden voor dit land.</p>}</div>
   </PopoverPanel>
  </Popover>
  {!value.length && <p className="text-xs text-slate-500 dark:text-slate-400">Alle ports van dit land</p>}
  {value.length > 0 && <ul className="flex flex-wrap gap-2">{value.map((item, index) => {
        const linked = candidates.some(port => port.portRuleId === item.portRuleId);
        return <li key={`${item.portRuleId}-${item.label}-${index}`} className={`flex max-w-full flex-wrap items-center gap-2 border border-slate-200 bg-white px-2 py-1.5 text-sm dark:border-white/10 dark:bg-slate-950 ${linked ? '' : 'w-full'}`}><span className="break-words">{item.label}</span>
    {!linked && <><span className="text-xs text-amber-700 dark:text-amber-400">Nog niet gekoppeld</span><select disabled={disabled} aria-label={`${item.label} koppelen`} value="" className="min-w-0 flex-1 border border-slate-300 bg-transparent p-1 dark:border-white/20" onChange={e => {
              const port = candidates.find(port => port.portRuleId === Number(e.target.value));
              if (!port) return;
              onChange(value.filter((other, otherIndex) => otherIndex === index || other.portRuleId !== port.portRuleId).map(other => other === item ? {
                portRuleId: port.portRuleId,
                label: port.label
              } : other));
            }}><option value="">Koppelen aan port</option>{candidates.map(port => <option key={port.portRuleId} value={port.portRuleId}>{port.label}</option>)}</select></>}
    <button type="button" disabled={disabled} aria-label={`${item.label} verwijderen`} onClick={() => onChange(value.filter((_, otherIndex) => otherIndex !== index))} className="ml-auto p-1 text-slate-500 hover:text-orange-600"><X className="h-4 w-4" /></button>
   </li>;
      })}</ul>}
  {!showManual ? <button type="button" disabled={disabled} onClick={()=>setShowManual(true)} className="text-sm font-medium text-orange-600 hover:underline disabled:opacity-50 dark:text-orange-400">Bestemming toevoegen</button> : <div className="flex flex-wrap items-end gap-3"><label className="min-w-0 flex-1 text-xs">Handmatige bestemming<input value={manual} maxLength={1000} disabled={disabled} onChange={e => setManual(e.target.value)} onKeyDown={e => {
          if (e.key === 'Enter') {
            e.preventDefault();
            addManual();
          }
        }} className={countryRuleControl} /></label><button type="button" disabled={disabled || !manual.trim()} onClick={addManual} className="min-h-10 border border-slate-300 px-3 text-sm disabled:opacity-50 dark:border-white/20">Toevoegen</button><button type="button" disabled={disabled} onClick={()=>{setShowManual(false);setManual('');setError('');}} className="min-h-10 px-2 text-sm text-slate-500">Annuleren</button></div>}
  {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
 </div>;
}
